class MegaMenu extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('.shopify-section-group-header-group');
    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');

      // extra events
      this.shadow?.addEventListener('click', this.closeAll.bind(this));

      // on key up
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    let group = this.querySelector(`[data-collapsible-group].${toggleClass}`);
    if (group) {
      this.close(group);
    }
  }

  open(group) {
    super.open(group);
    if (this.headerWrapper) {
      document.body.classList.add('header-is-open');
    }
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
    if (this.headerWrapper) {
      document.body.classList.remove('header-is-open');
    }
    this.toggleHeaderClass(false);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
    this.toggleHeaderClass(false);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
    if (this.headerWrapper) {
      document.body.classList.add('header-is-open');
    }
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
    if (this.headerWrapper) {
      document.body.classList.remove('header-is-open');
    }
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    if (open) {
      // set header open
      document.body.classList.add('header-is-open');
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        document.body.classList.remove('header-is-open');
      }
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');

      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener('click', (event) => {
        this.remove(event);
      });
    }
  }

  toggle() {
    if (document.querySelector('body.header-search-is-open')) {
      document.body.classList.remove('header-search-is-open');
    }
    super.toggle();
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('menu-drawer-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      document.body.classList.remove('menu-drawer-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('header-search-is-open');
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('header-search-is-open');
    }
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.headerElement = this;
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('#shopify-section-benefit-bar');
    this.headerNav = this.querySelector('mega-menu');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.productInfo = document.querySelector('[data-product-info]');

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.scrollOffset += announcementBarHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
      }

      window.addEventListener('scroll', () => {
        this.scrollAnimation();
      });
    }
  }

  /**
   * scrollAnimation
   */
  scrollAnimation() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    // only animate if header is closed.
    if (
      !document.body.classList.contains('header-is-open') &&
      !document.body.classList.contains('header-search-is-open')
    ) {
      if (scrollTop <= this.headerStart) {
        this.revealTop();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    document.body.classList.add('header-hidden', 'header-sticky');
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
  }

  reveal() {
    document.body.classList.add('header-sticky');
    document.body.classList.remove('header-hidden');
  }

  revealTop() {
    document.body.classList.remove('header-hidden', 'header-sticky');
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) {
      return false;
    }
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
